import React, {useEffect, useState, useRef} from "react";

import styles from "./RoomPage.module.css";
import logo from "./assets/pokerly-logo-purple.svg";
import footerBg from "./assets/footer-bg.svg";
import EstimatePanel from "./components/common/EstimatePanel";
import RoomSettingsModal from "./components/common/RoomSettingsModal/RoomSettingsModal";
import db from "./firebase";
import {doc, onSnapshot, runTransaction} from "firebase/firestore";
import {useTranslation} from "react-i18next";
import IconSettings from "./assets/Icon_settings.svg";
import AiTooltip from "./components/common/AiTooltip/AiTooltip";
import BurgerMenu from "./components/common/BurgerMenu/BurgerMenu";
import {joinRoom} from "./roomActions";
import ParticipantsList from "components/common/ParticipantsList/ParticipantsList";
import {Stats, VoteStats} from "utils/VoteStats";
import {VoteParser} from "utils/VoteParser";
import {getScaleValues} from "utils/scales"
import {Link} from "react-router-dom";


interface Participant {
    id: string;
    name: string;
    role: "facilitator" | "participant";
    estimate?: number | string | null;
}

interface RoomPageProps {
    roomId: string;
    userId: string;
    userName: string;
    roomName: string;
    estimationType: string;
    mode: "withFacilitator" | "withoutFacilitator";
    onRoomNameChange: (newName: string) => void;
    onEstimationTypeChange: (newType: string) => void;
}

const RoomPage: React.FC<RoomPageProps> = ({
                                               roomId,
                                               userId,
                                               userName,
                                               roomName,
                                               estimationType,
                                               mode,
                                               onRoomNameChange,
                                               onEstimationTypeChange
                                           }) => {
    const {t, i18n} = useTranslation();

    useEffect(() => {
        console.log("🌍 CURRENT LANGUAGE:", i18n.language);
        console.log("ai.highSpread:", t("ai.highSpread"));
        console.log("participantsCount:", t("participantsCount", {count: 3}));
        console.log("yourLabel:", t("yourLabel"));
    }, []);


    const [isRevealed, setIsRevealed] = useState(false);
    const [estimates, setEstimates] = useState<Record<string, number | string | null>>({});
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [selectedVote, setSelectedVote] = useState<string | number | null>(null);
    const [customValues, setCustomValues] = useState<string[]>([]);
    const [roomOwnerId, setRoomOwnerId] = useState("");


    // 🔄 локальные значения, синхронизируемые с Firebase
    const [currentRoomName, setCurrentRoomName] = useState(roomName);
    const [currentEstimationType, setCurrentEstimationType] = useState(estimationType);
    const [currentMode, setCurrentMode] = useState<"withFacilitator" | "withoutFacilitator">(mode);
    const isNumericScale = currentEstimationType !== "customString";


    const isFacilitator = participants.find(p => p.id === userId)?.role === "facilitator";
    const canRevealResults = currentMode === "withoutFacilitator" || (currentMode === "withFacilitator" && isFacilitator);
    const canResetResults = currentMode === "withoutFacilitator" || (currentMode === "withFacilitator" && isFacilitator);
    const hasJoinedRef = useRef(false);
    const prevEstimatesRef = useRef<Record<string, any>>({});

// 🔵 Сначала validVotes
// 🔵 Разделяем голоса на числовые и все заполненные
    const validNumericVotes = isRevealed
        ? Object.values(estimates).filter((v): v is number => typeof v === "number" && v !== null)
        : [];

    const validVotes = isRevealed
        ? Object.values(estimates).filter((v): v is string | number => v !== null)
        : [];


    const stats: Stats = isRevealed
        ? VoteStats.calcStatsFromVotes(estimates, getScaleValues(currentEstimationType, customValues))
        : {};

    const averageEstimate = VoteParser.formatMaybeNumber(stats.average);

    const medianEstimate = VoteParser.formatMaybeNumber(stats.median);

    const explanation = VoteStats.explainSpread(stats.spread, stats.range ?? null, t);

    const range = VoteStats.RangeClass(stats.range ?? null, t);

    useEffect(() => {
        const roomRef = doc(db, "rooms", roomId);
        const unsubscribe = onSnapshot(roomRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.owner) {
                    setRoomOwnerId(data.owner);
                }
                const newEstimates = data.estimates || {};
                handleRevoteSync(newEstimates);
                setEstimates(newEstimates);
                setParticipants(Object.values(data.participants || {}));
                setIsRevealed(data.isRevealed || false);

                // 🔄 синхронизация
                setCurrentRoomName(data.roomName || "");
                setCurrentEstimationType(data.estimationType || "fibonacci");
                setCurrentMode(data.mode || "withFacilitator");

                onRoomNameChange(data.roomName || t("untitledRoom"));
                onEstimationTypeChange(data.estimationType || "fibonacci");

                if (data.customValues && Array.isArray(data.customValues)) {
                    setCustomValues(data.customValues);
                }
            }
        });
        return () => unsubscribe();
    }, [roomId, onRoomNameChange, onEstimationTypeChange, t]);

    useEffect(() => {
        const addParticipantIfNeeded = async () => {
            if (!userId || !userName || hasJoinedRef.current) return;

            try {
                await joinRoom(roomId, userId, userName);
                hasJoinedRef.current = true;
            } catch (error) {
                console.error("Error entering the room " + roomId + ":", error);
            }
        };
        addParticipantIfNeeded();
    }, [roomId, userId, userName]);

    // Handle revote action triggered by other user
    const handleRevoteSync = (newEstimates: Record<string, number | string | null>) => {
        const prevEstimates = prevEstimatesRef.current;
        const wasNotEmpty = Object.keys(prevEstimates).length > 0;
        const isNowEmpty = Object.keys(newEstimates).length === 0;

        if (wasNotEmpty && isNowEmpty) {
            setSelectedVote(null);
        }
        prevEstimatesRef.current = newEstimates;
    };

    const handleVote = async (vote: string | number) => {
        const parsedVote = VoteParser.normalizeValue(vote);

        setSelectedVote(parsedVote);
        const roomRef = doc(db, "rooms", roomId);
        await runTransaction(db, async (transaction) => {
            const roomSnap = await transaction.get(roomRef);
            if (!roomSnap.exists()) {
                throw new Error("Room with id: " + roomId + " is not exist");
            }

            const estimatePath = `estimates.${userId}`;
            transaction.update(roomRef, {
                [estimatePath]: parsedVote
            });
        });
    };

    const handleRevealResults = async () => {
        const roomRef = doc(db, "rooms", roomId);
        await runTransaction(db, async (transaction) => {
            const roomSnap = await transaction.get(roomRef);
            if (!roomSnap.exists()) return;

            transaction.update(roomRef, {
                isRevealed: true
            });
        });
        setIsRevealed(true);
    };

    const handleResetVotes = async () => {
        const roomRef = doc(db, "rooms", roomId);
        await runTransaction(db, async (transaction) => {
            const roomSnap = await transaction.get(roomRef);
            if (!roomSnap.exists()) return;

            transaction.update(roomRef, {
                estimates: {},
                isRevealed: false
            });
        });
        setIsRevealed(false);
        setSelectedVote(null);
    };


    const groupedEstimates = Object.entries(estimates).reduce<Record<string, Participant[]>>((acc, [id, estimate]) => {
        if (estimate == null) return acc;
        const person = participants.find(p => p.id === id);
        if (!person) return acc;
        const key = estimate.toString();
        acc[key] = acc[key] || [];
        acc[key].push(person);
        return acc;
    }, {});

    const scale = getScaleValues(currentEstimationType, customValues);
    // сортировка голосов по шкале
    const scaleOrderedEntries: [string, Participant[]][] = scale
        .map((value) => [value, groupedEstimates[value] ?? []] as [string, Participant[]])
        .filter(([, group]) => group.length > 0);

    // значения, которых нет в шкале (на случай смены шкалы)
    const extraEntries: [string, Participant[]][] = Object.entries(groupedEstimates).filter(
        ([estimate]) => !scale.includes(estimate)
    );
    // итоговый порядок колонок
    const orderedEstimateEntries = [...scaleOrderedEntries, ...extraEntries];

    const [isActiveCopy, setIsActiveCopy] = useState(false);
    const copyButtonClick = (room: string) => {
        navigator.clipboard.writeText(room)

        setIsActiveCopy(true);

        setTimeout(() => {
            setIsActiveCopy(false);
        }, 1000);
    };
    return (
        <div className={styles.container}>
            <BurgerMenu/>
            <div className={styles.contentWrapper}>
                <div className={styles.logo}>
                    <Link to="/">
                        <img src={logo} alt="Pokerly Logo"/>
                    </Link>
                </div>
                <div className={styles.workspace}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.headerRow}>
                            <div className={styles.roomId}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M10.5998 4.31741C8.73558 2.85493 7.0253 0.971863 6.31608 0.157452C6.13325 -0.0524839 5.80734 -0.0524839 5.62452 0.157452C4.91527 0.97184 3.20502 2.8549 1.34081 4.31741C0.494994 4.98097 0 5.97874 0 7.03593C0 8.91223 1.56325 10.4333 3.4916 10.4333C4.14378 10.4333 4.73702 10.1881 5.17957 9.78739V10.4499C5.17957 11.2564 4.60158 11.3564 4.15309 11.733C4.04523 11.8236 4.11146 12 4.2523 12H7.67642C7.81677 12 7.88308 11.8248 7.77638 11.7336C7.3325 11.3542 6.76097 11.2695 6.76097 10.4559V9.78737C7.20352 10.1881 7.79677 10.4333 8.44895 10.4333C10.3773 10.4333 11.9405 8.91221 11.9405 7.0359C11.9406 5.97874 11.4456 4.98097 10.5998 4.31741Z"
                                        fill="#484848"/>
                                </svg>
                                {t("roomWithId", { id: roomId })}
                            </div>
                            <div className={styles.toolBtnGroup}>
                                <button
                                    className={styles.copyBtn}
                                    onClick={() => copyButtonClick(roomId)}
                                >
                                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M2 7H1.5C1.23478 7 0.98043 6.89464 0.792893 6.70711C0.605357 6.51957 0.5 6.26522 0.5 6V1.5C0.5 1.23478 0.605357 0.98043 0.792893 0.792893C0.98043 0.605357 1.23478 0.5 1.5 0.5H6C6.26522 0.5 6.51957 0.605357 6.70711 0.792893C6.89464 0.98043 7 1.23478 7 1.5V2M5 4H9.5C10.0523 4 10.5 4.44772 10.5 5V9.5C10.5 10.0523 10.0523 10.5 9.5 10.5H5C4.44772 10.5 4 10.0523 4 9.5V5C4 4.44772 4.44772 4 5 4Z"
                                            stroke="#9865DA" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    {t("copyRoomId")}
                                </button>
                                <div className={`${styles.copyTooltip} ${isActiveCopy ? styles.active : ''}`}>
                                    <svg width="8" height="5.5" viewBox="0 0 9 7" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.5 0.5L3 6L0.5 3.5" stroke="#5CC862" stroke-linecap="round"
                                              stroke-linejoin="round"/>
                                    </svg>
                                    {t("copy")}
                                </div>
                            </div>
                        </div>
                        <div className={styles.roomInfo}>
                            <p className={styles.roomName}>
                                {currentRoomName || t("untitledRoom")}
                            </p>
                            <button className={styles.toolBtn} onClick={() => setIsSettingsOpen(true)}>
                                <img src={IconSettings} alt="Settings"/>
                                <span>{t("settings")}</span>
                            </button>
                        </div>
                    </div>

                    {/* Voting */}
                    <div className={`${styles.voteBoxWrapper} ${styles[range]}`}>
                        {!isRevealed ? (
                            <>
                                <div className={styles.voteTitle}>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M19 13C19 13.5304 18.7893 14.0391 18.4142 14.4142C18.0391 14.7893 17.5304 15 17 15H5L1 19V3C1 2.46957 1.21071 1.96086 1.58579 1.58579C1.96086 1.21071 2.46957 1 3 1H17C17.5304 1 18.0391 1.21071 18.4142 1.58579C18.7893 1.96086 19 2.46957 19 3V13Z"
                                            stroke="#484848" stroke-width="2" stroke-linecap="round"
                                            stroke-linejoin="round"/>
                                    </svg>
                                    {selectedVote ? t("waitForReveal") : t("estimateTask")}
                                </div>

                                <EstimatePanel
                                    estimationType={currentEstimationType}
                                    customValues={customValues}
                                    onVote={handleVote}
                                    selectedVote={selectedVote}
                                    isRevealed={isRevealed}
                                    classes={{
                                        grid: styles.voteButtons,
                                        button: styles.voteButton,
                                        buttonSelected: styles.voteButtonSelected,
                                    }}
                                />


                            </>
                        ) : (
                            <>
                                {isNumericScale && validNumericVotes.length > 0 && (
                                    <p className={styles.resultTitle}>
                                        {t("averageEstimate")}: {averageEstimate}
                                    </p>
                                )}
                                <div className={styles.tooltipContainer}>
                                    <AiTooltip
                                        recommendedValue={stats.recommended}
                                        explanationText={explanation}
                                        average={stats.average}
                                        median={stats.median}
                                        spread={stats.spread}
                                        range={stats.range}
                                    />
                                </div>
                            </>
                        )}
                        <div className={styles.actions}>
                            <button
                                className={styles.toolBtn}
                                onClick={handleRevealResults}
                                disabled={!canRevealResults || isRevealed}
                            >
                                {t("revealResults")}
                            </button>
                            <button
                                className={styles.toolBtn}
                                onClick={handleResetVotes}
                                disabled={!canResetResults}>
                                <svg width="15" height="12" viewBox="0 0 15 12" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1.08337 1.33391V4.83391M1.08337 4.83391H4.58337M1.08337 4.83391L3.79004 2.29057C4.41698 1.66332 5.19259 1.20511 6.04451 0.958691C6.89643 0.712272 7.7969 0.685678 8.66187 0.88139C9.52685 1.0771 10.3282 1.48874 10.991 2.0779C11.6539 2.66706 12.1567 3.41453 12.4525 4.25057M13.9167 10.6672V7.16724M13.9167 7.16724H10.4167M13.9167 7.16724L11.21 9.71057C10.5831 10.3378 9.80749 10.796 8.95557 11.0425C8.10365 11.2889 7.20319 11.3155 6.33821 11.1198C5.47323 10.924 4.67192 10.5124 4.00906 9.92325C3.3462 9.33409 2.84339 8.58662 2.54754 7.75057"
                                        stroke="#3F3F3F" strokeWidth="1.16667" strokeLinecap="round"
                                        strokeLinejoin="round"/>
                                </svg>
                                <span>{t("revote")}</span>
                            </button>
                        </div>
                    </div>


                    {/* Participants */}
                    {!isRevealed && (
                        <ParticipantsList
                            participants={participants}
                            currentUserId={userId}
                            votedMap={Object.fromEntries(
                                Object.entries(estimates).map(([id, est]) => [id, est !== null])
                            )}
                        />
                    )}

                    {/* Estimate Columns — отдельно */}
                    {isRevealed && (
                        <div className={styles.estimateBoard}>
                            {orderedEstimateEntries.slice(0, 11).map(([estimate, group]) => {
                                const columnCount = Math.min(Object.keys(groupedEstimates).length, 11);
                                const widthPercent = Math.max(100 / 5, 100 / columnCount);

                                return (
                                    <div
                                        key={estimate}
                                        className={styles.estimateColumn}
                                    >
                                        <div className={styles.estimateColumnHeader}>{estimate}</div>
                                        <div className={styles.estimateParticipants}>
                                            {group.map((p) => (
                                                <div
                                                    key={p.id}
                                                    className={`${styles.participantCard} ${
                                                        p.id === userId ? styles.participantCardYou :
                                                            p.role === "facilitator" ? styles.participantCardFacilitator : ''
                                                    }`}
                                                >
                                                    <span className={styles.participantName}>{p.name}</span>
                                                    {p.id === userId && (
                                                        <span
                                                            className={`${styles.badge} ${styles.badgeYou}`}>{t("yourLabel")}</span>
                                                    )}
                                                    {p.role === "facilitator" && (
                                                        <span
                                                            className={`${styles.badge} ${styles.badgeFacilitator}`}>{t("facilitatorShort")}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.footer}>
                <img src={footerBg}/>
                <div className={styles.footerContent}>
                    <svg width="20" height="15" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M18.6894 3.63159C18.3967 2.94165 17.9576 2.32698 17.3722 1.78757C16.7995 1.23562 16.0677 0.802838 15.1768 0.48923C14.2986 0.163077 13.2678 0 12.0842 0H0V14.1124H7.94156V11.7227H7.96065L7.97974 11.7039H12.0842C13.2423 11.7039 14.2604 11.5471 15.1386 11.2335C16.0168 10.9073 16.7486 10.4683 17.334 9.91631C17.9321 9.36435 18.3776 8.73714 18.6703 8.03465C18.9758 7.31963 19.1285 6.56697 19.1285 5.77667C19.1285 5.03655 18.9821 4.32153 18.6894 3.63159ZM7.95174 9.47349L7.94156 9.49482L7.93011 9.47349C7.22504 7.99075 5.13784 7.42751 5.13784 5.83438C5.13784 4.4131 7.14995 3.85739 7.94156 5.28995C8.7319 3.85739 10.744 4.4131 10.744 5.83438C10.744 7.42751 8.65681 7.99075 7.95174 9.47349Z"
                            fill="#9B9B9B"/>
                    </svg>
                    <p>
                        {t("footerDescription")}
                    </p>
                </div>
                <p className={styles.copyright}>
                    2026 © Made with ❤️ from us. All rights reserved.
                </p>
            </div>
            <RoomSettingsModal
                open={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                roomName={currentRoomName}
                onRoomNameChange={onRoomNameChange}
                estimationType={currentEstimationType}
                onEstimationTypeChange={onEstimationTypeChange}
                mode={currentMode}
                onModeChange={setCurrentMode}
                showApplyButton
                roomId={roomId}
                participants={participants} // ✅ добавить
                userId={userId}             // ✅ добавить
                ownerId={roomOwnerId}
                roomType="poker"
            />
        </div>
    );
};

export default RoomPage;
